import {Component, EventEmitter, Input, Output} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Router} from '@angular/router';
import {MachineGroupService} from '@services/administration/machine-group/machine-group.service';
import {NotificationService} from '@services/notification/notification.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {ArrayOperationService} from '@services/administration/array-operation/array-operation.service';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';
import {ConfirmationDialogComponent} from '@shared/confirmation-dialog/confirmation-dialog.component';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {
  MachineGroupEditComponent
} from '@components/administration/machine-group/machine-group-edit/machine-group-edit.component';
import {Machine} from '@models/machine/machine';
import {User} from '@models/user/user';
import {MenuNotification, OperationType} from '@models/sidebar/menu-notification';
import {MachineGroup} from '@models/adminstration/machine-group/machine-group';
import {
  FavoriteNotificationService
} from '@services/left-menu/favorites/favorite-notification/favorite-notification.service';
import {ModalService} from '@services/modal/modal.service';
import {btou} from '@prototypes/global.functions';

@Component({
    selector: 'scitis-admin-machine-group-action',
    templateUrl: './machine-group-action.component.html',
    styleUrls: ['./machine-group-action.component.scss'],
    standalone: false
})
export class MachineGroupActionComponent {

  @Input()
  machinegroup: MachineGroup | any;

  @Input()
  rootMachinegroupId: number = 0;

  @Input()
  isAdminOrHigher: boolean = false;

  @Input()
  isSuperAdminRole: boolean = false;

  @Input()
  isDistributorRole: boolean = false;

  @Input()
  searchTerm: string = '';

  @Input()
  loggedUser: User = new User();

  @Output()
  machinegroupDeleteEvent = new EventEmitter<MachineGroup>();

  @Output()
  machineGroupUpdatedEvent = new EventEmitter<MachineGroup>();

  private COMPONENT_NAME: string = 'MachineGroupActionComponent';
  private CURRENT_PAGE_NAME = 'Machinery group action';

  constructor(
      private router: Router,
      private modalService: ModalService,
      private translate: TranslateService,
      private machineGroupService: MachineGroupService,
      private notification: NotificationService,
      private loadingIndicator: LoadingIndicatorService,
      private menuNotificationService: MenuNotificationService,
      private arrayOperationService: ArrayOperationService,
      private favoriteNotificationService: FavoriteNotificationService) {
  }

  private static copyMachineGroupProperties(source: MachineGroup, target: MachineGroup | null): MachineGroup {
    if (target == null) {
      target = new MachineGroup();
    }
    target.id = source.id;
    target.name = source.name;
    target.parentName = source.parentName;
    target.parentId = source.parentId;
    target.machines = source.machines;
    target.subMachineGroups = source.subMachineGroups;
    target.machines = [...source.machines];

    return target;
  }

  public onDeleteMachineGroup(machineGroup: MachineGroup): void {
    this.loadingIndicator.show(this.translate.instant('Verify if deletion is possible'));
    this.machineGroupService.getMachineryGroupDetails(machineGroup.id).subscribe({
      next: (machinegroup) => {
        const machines = machinegroup.machines;
        const users = machinegroup.users;
        const subMachineGroups = machinegroup.machineGroups;
        this.loadingIndicator.hide();
        if ((machines && machines.length > 0) || (users && users.length > 0) || (subMachineGroups && subMachineGroups.length > 0)) {
          this.showDeleteNotPossibleWarningModal(machinegroup, machines, users, subMachineGroups);
        } else {
          this.showDeleteWarningModalAndDelete(machinegroup);
        }
      }, error: (error) => {
        console.error(`${this.COMPONENT_NAME}. error loading machines, users or sub-machinery groups of machinery group`, error);
        this.loadingIndicator.hide();
      }
    });
  }

  public onEditMachineGroup(machineGroup: MachineGroup): void {
    this.loadingIndicator.show(this.translate.instant('Loading machinery group details'));
    this.machineGroupService.getMachineryGroupDetails(machineGroup.id).subscribe({
      next: (machinegroup) => {
        this.loadingIndicator.hide();
        const machineGroupBackup = MachineGroupActionComponent.copyMachineGroupProperties(machinegroup, null);
        const modalRef = this.modalService.open(MachineGroupEditComponent, {size: 'lg'});
        modalRef.componentInstance.isSuperAdminRole = this.isSuperAdminRole;
        modalRef.componentInstance.isAdminOrHigher = this.isAdminOrHigher;
        modalRef.componentInstance.isDistributorRole = this.isDistributorRole;
        modalRef.componentInstance.rootMachineGroupId = this.rootMachinegroupId;
        modalRef.componentInstance.loggedUser = this.loggedUser;

        modalRef.componentInstance.machineGroup = machinegroup;

        modalRef.result.then((result) => {
          const nameHasChanged = result.name !== machineGroupBackup.name;
          const parentHasChanged = result.parentId !== machineGroupBackup.parentId;
          const machineListHasChanged = !this.arrayOperationService.arraysAreIdentical(result.machines, machineGroupBackup.machines);
          console.log(`Closed with: ${result.name}`);
          this.machinegroup = MachineGroupActionComponent.copyMachineGroupProperties(result, this.machinegroup);
          this.loadingIndicator.show(this.translate.instant('Saving machinery group'));
          this.machineGroupService.updateMachineryGroup(result).subscribe({
            next: () => {
              if (nameHasChanged) {
                this.menuNotificationService.broadcast(new MenuNotification(OperationType.UPDATE_NAME, String(result.id), result.name, result.name));
                this.favoriteNotificationService.broadcast(new MenuNotification(OperationType.UPDATE_NAME, String(result.id), '', result.name));
              }
              if (parentHasChanged) {
                this.menuNotificationService.broadcast(new MenuNotification(OperationType.UPDATE_USER_MACHINERY_GROUP, String(result.id), result.name, result.name));
                this.favoriteNotificationService.broadcast(new MenuNotification(OperationType.UPDATE_USER_MACHINERY_GROUP, String(result.id), '', result.name));
                this.favoriteNotificationService.broadcast(new MenuNotification(OperationType.UPDATE_USER_MACHINERY_GROUP, String(machineGroupBackup.parentId), result.parentId, result.parentName));
              }
              if (machineListHasChanged) {
                this.menuNotificationService.broadcast(new MenuNotification(OperationType.UPDATE_IN_MACHINES_LIST, String(result.id), result.name, ''));
              }
              const message = this.translate.instant('Machinery group') + ' ' + result.name + ' ' +
                  this.translate.instant('updated') + '.';
              this.notification.success(message, this.COMPONENT_NAME, false);
              this.loadingIndicator.hide();
            }, error: (error) => {
              console.log(error);
              this.notification.error(this.translate.instant('Error updating machinery group') + ': ' + result.name,
                  error, this.COMPONENT_NAME, true);
              this.machinegroup = MachineGroupActionComponent.copyMachineGroupProperties(machineGroupBackup, this.machinegroup);
              this.loadingIndicator.hide();
            }
          });
        }, () => {
          this.machinegroup = MachineGroupActionComponent.copyMachineGroupProperties(machineGroupBackup, this.machinegroup);
        });
      }, error: error => {
        console.log(error);
        const message = this.translate.instant('Error getting machinery group details');
        this.notification.error(message, error, this.CURRENT_PAGE_NAME, true);
        this.loadingIndicator.hide();
      }
    });

  }

  public onRedirectToConfiguration(machineGroup: MachineGroup) {
    const parameters = {
      id: machineGroup.id,
      user: this.loggedUser
    };

    const encodedParams = btou(JSON.stringify(parameters));
    this.router.navigate(
        ['machinegroups', encodedParams],
        {state: {search: this.searchTerm}}
    );
  }

  private showDeleteNotPossibleWarningModal(machinegroup: MachineGroup, machines: Machine[], users: User[], subMachineGroups: MachineGroup[]) {
    const title = this.translate.instant('Delete machinery group');
    const body = this.createMachineryGroupRemoveWarningBody(machinegroup, machines, users, subMachineGroups);
    const modalRef = this.modalService.open(ConfirmationDialogComponent);
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.body = body;
    modalRef.componentInstance.cancelButtonText = null;
    modalRef.componentInstance.okButtonText = this.translate.instant('Close');

    modalRef.result.then();
  }

  private createMachineryGroupRemoveWarningBody(machinegroup, machines, users, subMachineGroups) {
    let body: string = this.translate.instant('The machinery group: %s cannot be removed since it has users or machines assigned or has machinery group descendants.');
    body = body.replaceAll('%s', machinegroup.name.toBold()) + '<br/>';

    if (subMachineGroups && subMachineGroups.length > 0) {
      body += '<br/>' + this.translate.instant('Machinery groups').toBold() +
          ':<div style="overflow-y: auto;max-height: 200px;">' +
          subMachineGroups.map(function (elem) {
            return elem.name;
          }).join(', ') + '</div>';
    }
    if (machines && machines.length > 0) {
      body += '<br/>' + this.translate.instant('Machines').toBold() +
          ':<div style="overflow-y: auto;max-height: 200px;">' +
          machines.map(function (elem) {
            return elem.name;
          }).join(', ') + '</div>';
    }
    if (users && users.length > 0) {
      body += '<br/>' + this.translate.instant('Users').toBold() +
          ':<div style="overflow-y: auto;max-height: 200px;">' +
          users.map(function (elem) {
            return elem.username;
          }).join(', ') + '</div>';
    }
    return body;
  }

  private showDeleteWarningModalAndDelete(machineGroup: MachineGroup) {
    console.log(`${this.COMPONENT_NAME}. removing machinery group: ${machineGroup.name}`);
    const removeMessageTitle = this.translate.instant('Delete machinery group');
    const removeMessageBody = this.translate.instant('Do you really want to permanently delete machinery group %s ?').replace('%s', machineGroup.name.toBold());
    const removeMessageOK = this.translate.instant('Delete');
    const modalRef = this.modalService.open(ConfirmationDialogComponent);
    modalRef.componentInstance.title = removeMessageTitle;
    modalRef.componentInstance.body = removeMessageBody;
    modalRef.componentInstance.cancelButtonText = '';
    modalRef.componentInstance.okButtonText = removeMessageOK;

    modalRef.result.then(() => {
    }, (reason) => {
      if (reason === ModalDialogDismissOption.CANCEL || reason === ModalDialogDismissOption.CLOSE) {
        return;
      }
      this.loadingIndicator.show(this.translate.instant('Deleting machinery group'));
      this.machineGroupService.delete(machineGroup.id).subscribe({
        next: () => {
          this.menuNotificationService.broadcast(new MenuNotification(OperationType.DELETE, String(machineGroup.id), machineGroup.name, ''));
          this.favoriteNotificationService.broadcast(new MenuNotification(OperationType.DELETE, String(machineGroup.id), '', ''));
          this.notification.success(this.translate.instant('Machinery group') + ' ' + machineGroup.name + ' ' +
              this.translate.instant('deleted') + '.', this.COMPONENT_NAME, false);
          this.machinegroupDeleteEvent.emit(machineGroup);
          this.loadingIndicator.hide();
        }, error: (error) => {
          this.notification.error(this.translate.instant('Machinery group could not be deleted') + ': ' +
              machineGroup.name, error, this.COMPONENT_NAME, true);
          this.loadingIndicator.hide();
        }
      });
    });
  }
}
